/**
 * Who may open what. The services decide what a request may do; this table
 * only decides which sections the interface offers, so that nobody is shown a
 * door that answers `403`.
 */
import { ProblemError } from "@/shared/api/problem";

import type { StaffRole } from "./roles";

/** The sections of the control center, in sidebar order. Each is a page at `/<section>`. */
export const SECTIONS = [
  "now",
  "orders",
  "specialists",
  "clients",
  "money",
  "catalog",
  "growth",
  "settings",
] as const;
export type Section = (typeof SECTIONS)[number];

export type SectionRoles = Readonly<Record<Section, readonly StaffRole[]>>;

const EVERY_STAFF_ROLE: readonly StaffRole[] = ["superadmin", "admin", "dispatcher"];
const ADMINS: readonly StaffRole[] = ["superadmin", "admin"];

/**
 * The model book's table of roles (chapter 24): a dispatcher has the orders,
 * and reads specialists and clients; an admin and a superadmin have every
 * section. What a role may do inside a section is hg-auth's to say
 * (`permissions`, `./permissions.ts`); its matrix has no rows for reading,
 * so which section a role is offered stays here. Narrow a section and both
 * the sidebar and the route follow.
 */
const SECTION_ROLES: SectionRoles = {
  now: EVERY_STAFF_ROLE,
  orders: EVERY_STAFF_ROLE,
  specialists: EVERY_STAFF_ROLE,
  clients: EVERY_STAFF_ROLE,
  money: ADMINS,
  catalog: ADMINS,
  growth: ADMINS,
  settings: ADMINS,
};

/** No role, or a role this build does not know, opens nothing. */
export function canOpen(
  role: StaffRole | null,
  section: Section,
  table: SectionRoles = SECTION_ROLES,
): boolean {
  return role !== null && table[section].includes(role);
}

/**
 * The screen a person starts on (model book, chapter 24): a dispatcher on the
 * orders board, where their work is; an admin and a superadmin on what is
 * going on now. Somebody without a role is sent where they will be told so.
 */
export function startSection(role: StaffRole | null): Section {
  return role === "dispatcher" ? "orders" : "now";
}

/** For a route's `beforeLoad`: stops with the services' own `forbidden`. */
export function requireSection(role: StaffRole | null, section: Section): void {
  if (!canOpen(role, section)) {
    throw new ProblemError({ status: 403, code: "forbidden", title: "Forbidden" });
  }
}
